import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, XCircle, Shield, AlertTriangle, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Applicant } from "@/types/applicant";
import EmailPreviewStep from "./EmailPreviewStep";
import { generateRejectionPreviewHtml } from "@/lib/emailPreviewTemplates";

const REJECTION_REASONS = [
  { value: "qualification", label: "Qualifikation passt nicht", description: "Die fachlichen Qualifikationen entsprechen nicht den Anforderungen der Stelle." },
  { value: "position_filled", label: "Stelle besetzt durch anderen Kandidaten", description: "Wir haben uns für einen anderen Kandidaten entschieden, der besser zu unseren aktuellen Anforderungen passt." },
  { value: "no_position", label: "Derzeit keine offene Stelle", description: "Vielen Dank für Ihre Anfrage. Zurzeit suchen wir in diesem Bereich keine neuen Mitarbeiter." },
  { value: "overqualified", label: "Überqualifiziert für die Stelle", description: "Ihre Qualifikationen übersteigen die Anforderungen dieser Position." },
  { value: "other", label: "Sonstiges", description: "" },
] as const;

interface RejectionEmailDialogProps {
  applicant: Applicant;
  onSuccess?: () => void;
  onStageChange?: (stage: "rejected", notes?: string) => void;
}

const RejectionEmailDialog = ({
  applicant,
  onSuccess,
  onStageChange,
}: RejectionEmailDialogProps) => {
  const [open, setOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customReasonText, setCustomReasonText] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [deleteDocuments, setDeleteDocuments] = useState(true);
  const [futureContact, setFutureContact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [companyName, setCompanyName] = useState("Unser Unternehmen");
  const [companyEmail, setCompanyEmail] = useState<string | undefined>();
  const [companyInfo, setCompanyInfo] = useState<{ logoUrl?: string; street?: string; houseNumber?: string; postalCode?: string; city?: string; phone?: string }>({});

  useEffect(() => {
    if (open) {
      supabase.from("company_settings").select("name, email, logo_url, street, house_number, postal_code, city, phone").limit(1).maybeSingle().then(({ data }) => {
        if (data?.name) setCompanyName(data.name);
        if (data?.email) setCompanyEmail(data.email);
        setCompanyInfo({
          logoUrl: data?.logo_url || undefined,
          street: data?.street || undefined,
          houseNumber: data?.house_number || undefined,
          postalCode: data?.postal_code || undefined,
          city: data?.city || undefined,
          phone: data?.phone || undefined,
        });
      });
    }
  }, [open]);

  const getFullReasonText = () => {
    if (!selectedReason) return "";
    if (selectedReason === "other") return customReasonText;
    const reasonObj = REJECTION_REASONS.find(r => r.value === selectedReason);
    return reasonObj ? `${reasonObj.label}: ${reasonObj.description}` : "";
  };

  const handleShowPreview = () => setShowPreview(true);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const fullReason = getFullReasonText();
      const { error } = await supabase.functions.invoke("send-rejection-email", {
        body: {
          applicantId: applicant.id,
          reason: fullReason || undefined,
          customMessage: customMessage || undefined,
          deleteDocuments,
          futureContact,
        },
      });
      if (error) throw error;
      if (onStageChange) onStageChange("rejected", "Absage per E-Mail gesendet");
      toast.success("Absage erfolgreich gesendet");
      setOpen(false);
      setShowPreview(false);
      onSuccess?.();
      setSelectedReason("");
      setCustomReasonText("");
      setCustomMessage("");
      setDeleteDocuments(true);
      setFutureContact(false);
    } catch (error: any) {
      console.error("Error sending rejection:", error);
      toast.error("Fehler beim Senden der Absage");
    } finally {
      setLoading(false);
    }
  };

  const applicantName = `${applicant.first_name} ${applicant.last_name}`;
  const previewHtml = generateRejectionPreviewHtml(applicantName, companyName, customMessage || undefined, deleteDocuments, companyEmail, companyInfo, futureContact);
  const previewSubject = `Ihre Bewerbung bei ${companyName}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setShowPreview(false); }}>
      <DialogTrigger asChild>
        <Button variant="destructive" className="w-full">
          <XCircle className="h-4 w-4 mr-2" />
          Absage senden
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <XCircle className="h-5 w-5" />
            Absage senden
          </DialogTitle>
          <DialogDescription>
            Senden Sie eine freundliche Absage an den Bewerber.
          </DialogDescription>
        </DialogHeader>

        {showPreview ? (
          <EmailPreviewStep
            html={previewHtml}
            subject={previewSubject}
            recipientEmail={applicant.email}
            onBack={() => setShowPreview(false)}
            onConfirmSend={handleSubmit}
            loading={loading}
            sendButtonLabel="Absage senden"
            sendButtonVariant="destructive"
            loadingIcon={<Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          />
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{applicantName}</p>
                    <p className="text-sm text-muted-foreground">{applicant.email}</p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>Absagegrund</Label>
                  <Select value={selectedReason} onValueChange={setSelectedReason}>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Absagegrund auswählen..." />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      {REJECTION_REASONS.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>{reason.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedReason && selectedReason !== "other" && (
                    <p className="text-xs text-muted-foreground">
                      {REJECTION_REASONS.find(r => r.value === selectedReason)?.description}
                    </p>
                  )}
                </div>

                {selectedReason === "other" && (
                  <div className="grid gap-2">
                    <Label>Eigener Absagegrund</Label>
                    <Textarea placeholder="Geben Sie hier Ihren eigenen Absagegrund ein..." value={customReasonText} onChange={(e) => setCustomReasonText(e.target.value)} rows={2} />
                  </div>
                )}
                <p className="text-xs text-muted-foreground -mt-2">Der Absagegrund wird intern gespeichert und kann optional in der E-Mail verwendet werden.</p>

                <div className="grid gap-2">
                  <Label>Persönliche Nachricht (optional)</Label>
                  <Textarea placeholder="z.B. Wir danken Ihnen für Ihr Interesse..." value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} rows={3} />
                  <p className="text-xs text-muted-foreground">Diese Nachricht wird in der Absage-E-Mail angezeigt.</p>
                </div>

                {selectedReason === "no_position" && (
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg border border-blue-200 dark:border-blue-900">
                    <div className="flex items-start space-x-3">
                      <Checkbox id="futureContact" checked={futureContact} onCheckedChange={(checked) => setFutureContact(checked as boolean)} />
                      <div className="grid gap-1.5 leading-none">
                        <label htmlFor="futureContact" className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                          📬 Zukünftige Kontaktaufnahme anbieten
                        </label>
                        <p className="text-xs text-muted-foreground">Der Bewerber wird in der E-Mail gefragt, ob er bei zukünftigem Bedarf erneut kontaktiert werden möchte.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-muted/50 p-4 rounded-lg border">
                  <div className="flex items-start space-x-3">
                    <Checkbox id="deleteDocuments" checked={deleteDocuments} onCheckedChange={(checked) => setDeleteDocuments(checked as boolean)} />
                    <div className="grid gap-1.5 leading-none">
                      <label htmlFor="deleteDocuments" className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                        <Shield className="h-4 w-4 text-blue-600" />
                        DSGVO-konforme Datenlöschung
                      </label>
                      <p className="text-xs text-muted-foreground">Alle Bewerbungsunterlagen werden nach dem Versand der Absage automatisch gelöscht.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={handleShowPreview} variant="outline">
                <Eye className="h-4 w-4 mr-2" />
                Vorschau
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RejectionEmailDialog;
